import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { Availability } from '../../entities/Availability/index.js';
import { LanguageOptions } from '../../entities/LanguageOptions/index.js';
import { Costs } from '../../entities/Costs/index.js';
import { toast } from 'react-toastify';
import { hasChangesAvailability } from '../../shared/utils/compareAvailability.js';
import { Form, Row, Col } from 'react-bootstrap';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

const EditPromocodePage = () => {
  const [promocodeValue, setPromocodeValue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [oldData, setOldData] = useState(null);
  const [form, setForm] = useState(null);
  const toastRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const handleDeletePrice = (countryId, item) => {
    setForm((prev) => {
      const costs = prev[item].costs.filter((i) => i.countryId !== countryId);
      return { ...prev, [`${item}`]: { costs } };
    });
  };

  const handleChangePrice = (value, changedItem) => {
    setForm((prev) => {
      const newPriceFrom = prev[changedItem]?.costs.map((i) => {
        if (i.countryId === value.countryId) {
          return {
            countryId: i.countryId,
            costs: i.costs.map((z) => {
              if (z.currencyId === value.value.currencyId) {
                return { currencyId: z.currencyId, value: value.value.newValue };
              } else {
                return z;
              }
            }),
          };
        } else {
          return i;
        }
      });
      return { ...prev, [changedItem]: { costs: newPriceFrom } };
    });
  };

  const handleChangeType = () => {
    setForm((prev) => {
      if (prev.discriminator === 'PricePromocode') {
        setPromocodeValue([]);
        return { ...prev, discriminator: 'PercentPromocode' };
      } else {
        setPromocodeValue(0);
        return { ...prev, discriminator: 'PricePromocode' };
      }
    });
  };

  const handleChangePriceOfPromocode = (value) => {
    setPromocodeValue(() => {
      const newPriceFrom = promocodeValue.map((i) => {
        if (i.countryId === value.countryId) {
          return {
            countryId: i.countryId,
            costs: i.costs.map((z) => {
              if (z.currencyId === value.value.currencyId) {
                return { currencyId: z.currencyId, value: value.value.newValue };
              } else {
                return z;
              }
            }),
          };
        } else {
          return i;
        }
      });

      return newPriceFrom;
    });
  };

  const handleRequest = async () => {
    toastRef.current = toast('Идёт запрос к серверу', { autoClose: false });
    try {
      if (!form.code) {
        toast.update(toastRef.current, {
          render: 'Введите код промокода',
          type: 'error',
          autoClose: 5000,
        });
        return;
      }
      
      let discountValue;
      
      if (form.discriminator === 'PricePromocode') {
        if (!promocodeValue || promocodeValue.length === 0) {
          toast.update(toastRef.current, {
            render: 'Укажите сумму скидки',
            type: 'error',
            autoClose: 5000,
          });
          return;
        }
        discountValue = { costs: promocodeValue };
      } else {
        if (!promocodeValue && promocodeValue !== 0) {
          toast.update(toastRef.current, {
            render: 'Укажите процент скидки',
            type: 'error',
            autoClose: 5000,
          });
          return;
        }
        discountValue = Number(promocodeValue);
        if (isNaN(discountValue)) {
          toast.update(toastRef.current, {
            render: 'Процент скидки должен быть числом',
            type: 'error',
            autoClose: 5000,
          });
          return;
        }
        if (discountValue < 0 || discountValue > 100) {
          toast.update(toastRef.current, {
            render: 'Процент скидки должен быть от 0 до 100',
            type: 'error',
            autoClose: 5000,
          });
          return;
        }
      }
      
      const body = {
        ...form,
        [form.discriminator === 'PricePromocode' ? 'discount' : 'percent']: discountValue,
      };
      
      if (hasChangesAvailability(oldData.availability, form.availability)) {
        await apiRequests.products.updateAvailability(form.availability);
      }

      const query = await apiRequests.promocodes.update(body);
      toast.update(toastRef.current, {
        render: 'Промокод успешно изменён',
        type: 'success',
        autoClose: 5000,
      });
      
      setTimeout(() => {
        navigate('/shop/promocodes');
      }, 1000);
    } catch (e) {
      toast.update(toastRef.current, {
        render: e.error || 'Произошла ошибка при редактировании промокода',
        type: 'error',
        autoClose: 5000,
      });
      console.error('error promocode: ', e);
    }
  };

  const getInitialPrices = useCallback(
    (type) => {
      if (!loading) {
        const result = form[type].costs.map((i) => {
          const costs = i.costs.map((z) => {
            return { currencyId: z.currencyId, value: z.value };
          });
          return { countryId: i.countryId, costs };
        });

        return result;
      }
    },
    [loading],
  );

  useEffect(() => {
    const getData = () => {
      apiRequests.promocodes.getById(id).then((res) => {
        setForm(res);
        setOldData(res);
        if (res.discriminator === 'PricePromocode') {
          setPromocodeValue(
            res.discount.costs.map((i) => {
              const costs = i.costs.map((z) => {
                return { currencyId: z.currencyId, value: z.value };
              });
              return { countryId: i.countryId, costs };
            }),
          );
        } else {
          setPromocodeValue(res.percent);
        }
        setLoading(false);
      });
    };
    getData();
  }, []);

  if (loading) return <FormLoading />;

  const initialPricesFrom = getInitialPrices('priceFrom');
  const initialPricesTo = getInitialPrices('priceTo');

  return (
    <FormLayout
      onSubmit={handleRequest}
      onBack={() => navigate('/shop/promocodes')}
      submitText="Сохранить"
    >
      <FormSection title="Основная информация">
        <Row>
          <Col md={6}>
            <FormField label="Промокод" required>
              <Form.Control
                type="text"
                value={form.code}
                onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
                placeholder="Введите промокод"
              />
            </FormField>
          </Col>
          <Col md={3}>
            <FormField label="Повторное использование">
              <Form.Select
                value={form.isReusable}
                onChange={(e) => setForm((prev) => ({ ...prev, isReusable: e.target.value === 'true' }))}
              >
                <option value="true">Да</option>
                <option value="false">Нет</option>
              </Form.Select>
            </FormField>
          </Col>
          <Col md={3}>
            <FormField label="Тип промокода">
              <Form.Select
                value={form.discriminator}
                onChange={handleChangeType}
              >
                <option value="PricePromocode">Фиксированный</option>
                <option value="PercentPromocode">Проценты</option>
              </Form.Select>
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Период действия">
        <Row>
          <Col md={6}>
            <FormField label="Действует с">
              <Form.Control
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
              />
            </FormField>
          </Col>
          <Col md={6}>
            <FormField label="Действует до">
              <Form.Control
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title={form.discriminator === 'PricePromocode' ? 'Сумма скидки' : 'Процент скидки'}>
        {form.discriminator === 'PricePromocode' ? (
          <Costs
            initialValues={promocodeValue}
            onChange={(val) => handleChangePriceOfPromocode(val)}
            onAdd={(value) => setPromocodeValue((prev) => [...prev, value])}
          />
        ) : (
          <FormField label="Процент">
            <Form.Control
              type="number"
              min="0"
              max="100"
              value={promocodeValue}
              onChange={(e) => setPromocodeValue(e.target.value)}
              placeholder="Введите значение"
              style={{ maxWidth: '150px' }}
            />
          </FormField>
        )}
      </FormSection>

      <FormSection title="Условия применения">
        <FormField label="Действует от суммы">
          <Costs
            initialValues={initialPricesFrom}
            onChange={(val) => handleChangePrice(val, 'priceFrom')}
            onDelete={(val) => handleDeletePrice(val, 'priceFrom')}
            onAdd={(value) =>
              setForm((prev) => ({
                ...prev,
                priceFrom: !prev.priceFrom
                  ? { costs: [value] }
                  : { costs: [...prev.priceFrom.costs, value] },
              }))
            }
          />
        </FormField>

        <FormField label="Действует до суммы">
          <Costs
            initialValues={initialPricesTo}
            onDelete={(val) => handleDeletePrice(val, 'priceTo')}
            onChange={(val) => handleChangePrice(val, 'priceTo')}
            onAdd={(value) =>
              setForm((prev) => ({
                ...prev,
                priceTo: !prev.priceTo
                  ? { costs: [value] }
                  : { costs: [...prev.priceTo.costs, value] },
              }))
            }
          />
        </FormField>
      </FormSection>

      <FormSection title="Настройки">
        <Row>
          <Col lg={6}>
            <LanguageOptions
              initialValue={form.mainLanguageId}
              onSelect={(value) => setForm((prev) => ({ ...prev, mainLanguageId: value }))}
            />
          </Col>
        </Row>

        <div className="mt-4">
          <Form.Label className="fw-medium mb-3">Доступность по странам</Form.Label>
          <Availability
            initialValues={form.availability}
            onSelect={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, availableInAllCountries: val },
              }))
            }
            onSelectAllowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, allowedCountriesIds: val },
              }))
            }
            onSelectDisallowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, disallowedCountriesIds: val },
              }))
            }
          />
        </div>
      </FormSection>
    </FormLayout>
  );
};

export default EditPromocodePage;
